import { useState, useEffect } from 'react';
import { withWindow } from '@shared/utils/withWindow';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Tracks the user's OS-level `prefers-reduced-motion` setting.
 *
 * Initializes synchronously on the client (via `withWindow`, so it stays
 * SSR-safe and avoids a first-paint flash of motion) and updates whenever the
 * setting changes. Use it to suppress involuntary motion (e.g. autoplaying
 * animations) while leaving user-initiated motion (hover, click) intact.
 *
 * @returns `true` if the user prefers reduced motion
 */
export function usePrefersReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    return withWindow(win => (typeof win.matchMedia === 'function' ? win.matchMedia(REDUCED_MOTION_QUERY).matches : false), false);
  });

  useEffect(() => {
    return withWindow(win => {
      if (typeof win.matchMedia !== 'function') return undefined;
      const mediaQuery = win.matchMedia(REDUCED_MOTION_QUERY);

      const handleChange = (event: MediaQueryListEvent) => {
        setPrefersReducedMotion(event.matches);
      };

      // Modern browsers use addEventListener, older ones (Safari < 14) use addListener
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange);
      } else {
        mediaQuery.addListener(handleChange);
      }

      // Sync state in case it changed between render and effect
      setPrefersReducedMotion(mediaQuery.matches);

      return () => {
        if (mediaQuery.removeEventListener) {
          mediaQuery.removeEventListener('change', handleChange);
        } else {
          mediaQuery.removeListener(handleChange);
        }
      };
    });
  }, []);

  return prefersReducedMotion;
}
